import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateChild, Router, RouterStateSnapshot } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { MenuService } from '../../core/menu/menu.service';
import { AuthService } from '@shared/services/auth/auth.service';
import { EnableEngageCookieService } from '@shared/services/auth/enable-engage-cookie.service';
import { EngageHistoryMode, EngageRedirectService } from '../../layout/header/engage-redirect.service';
import { Profile } from '@state/reducers/app/app.model';
import { IState } from '@state/state.module';
import { Logger } from '@services/logging/logger';

export const LOGIN_BACKDOOR_URL_KEY = 'login_backdoor_url';

/**
 * While a tenant user prefers Engage, every Classic page that has an Engage counterpart opens in Engage
 * instead. It guards the children of the logged-in layout, so it also sees navigations inside a lazy
 * module (e.g. between the tabs of an event). Arrivals through the login backdoor are an explicit
 * request for a Classic page and are never sent back to Engage.
 */
@Injectable()
export class EngageRedirectGuard implements CanActivateChild {

    private lastEvaluatedNavigationId: number | undefined;
    private pendingRedirect: Subscription | undefined;

    constructor(
        private readonly enableEngage: EnableEngageCookieService,
        private readonly authService: AuthService,
        private readonly menu: MenuService,
        private readonly store: Store<IState>,
        private readonly engageRedirect: EngageRedirectService,
        private readonly router: Router
    ) {}

    public canActivateChild(_childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
        if (this.isAlreadyEvaluated()) {
            return true;
        }

        this.pendingRedirect?.unsubscribe();

        if (!this.enableEngage.read()) {
            return true;
        }

        if (sessionStorage.getItem(LOGIN_BACKDOOR_URL_KEY)) {
            return true;
        }

        return !this.redirectToEngageWhenAllowed(state.url);
    }

    // Angular asks a parent's canActivateChild once per activated descendant, so a single navigation
    // can reach this guard several times.
    private isAlreadyEvaluated(): boolean {
        const navigationId = this.router.getCurrentNavigation()?.id;
        if (navigationId === this.lastEvaluatedNavigationId) {
            return true;
        }

        this.lastEvaluatedNavigationId = navigationId;
        return false;
    }

    // Returns true when the redirect was decided synchronously, so the Classic navigation can be
    // cancelled. Right after login the tenant flag is still loading; the redirect then follows once it
    // arrives, after the Classic page has activated and entered the history. That late redirect
    // replaces the history entry, otherwise Back from Engage reloads the Classic page and redirects again.
    private redirectToEngageWhenAllowed(classicUrl: string): boolean {
        let isRedirecting = false;
        let isSynchronous = true;
        this.pendingRedirect = combineLatest([
            this.menu.engagePermissionLoaded$,
            this.profileLoaded$()
        ])
            .pipe(take(1))
            .subscribe(([hasEngagePermission, profile]) => {
                const historyMode = isSynchronous ? EngageHistoryMode.Push : EngageHistoryMode.Replace;
                isRedirecting = this.redirectIfTenantUserPrefersEngage(classicUrl, hasEngagePermission, profile, historyMode);
            });
        isSynchronous = false;

        return isRedirecting;
    }

    private redirectIfTenantUserPrefersEngage(classicUrl: string, hasEngagePermission: boolean, profile: Profile, historyMode: EngageHistoryMode): boolean {
        if (profile.isSupport || !hasEngagePermission) {
            return false;
        }

        const tenantId = this.authService.retrieveToken()?.decoded?.ten;
        if (!tenantId) {
            Logger.warn('EngageRedirectGuard.redirectIfTenantUserPrefersEngage: tenantId missing, cannot redirect to Engage');
            return false;
        }

        return this.engageRedirect.redirectToEngage(classicUrl, tenantId, 'EngageRedirectGuard.redirectIfTenantUserPrefersEngage', historyMode);
    }

    private profileLoaded$(): Observable<Profile> {
        return this.store.select(appState => appState.app.profile)
            .pipe(filter(profile => !profile.pending));
    }
}
